import type { Dispatch, UnknownAction } from "@reduxjs/toolkit";
import { ablyChannelNamesForSession } from "_shared/_stores/realtime/ably-channel-names";
import reduxAblyMiddleware, { withCallbacks } from "_shared/_stores/realtime/redux-ably-middleware";
import { documentsApi } from "documents/_api/document-api";
import Guid from "_shared/guids/guid";
import { proposalsApi } from "work/proposals/_api/proposal-api";
import { cleanupDeletedDocumentReferences as cleanupProposalDeletedDocumentReferences } from "work/proposals/_store/proposals-redux-slice";
import { workAgreementsApi } from "work/work-agreements/_api/work-agreement-api";
import { cleanupDeletedDocumentReferences as cleanupWorkAgreementDeletedDocumentReferences } from "work/work-agreements/_store/work-agreement-redux-slice";

/** The ids an id-only event names, or nothing when the payload is not a list of ids. */
const toDocumentIds = (payload: unknown): Guid[] => {
  try {
    return Guid.fromJSONArray(payload) ?? [];
  } catch {
    return [];
  }
};

/**
 * Invalidates each document a batch event names by id, and the lists. An id-only event says that
 * something changed without saying what, so each page reads the documents again through the read
 * that decides what this reader may be told.
 */
const invalidateDocumentIds = (payload: unknown) => (dispatch: Dispatch<UnknownAction>) => {
  const documentIds = toDocumentIds(payload);
  if (!documentIds.length) return;
  dispatch(documentsApi.util.invalidateTags([
    ...documentIds.map((id) => ({ type: "Document" as const, id: id.value })),
    { type: "Document", id: "LIST" },
  ]));
};

// Every event on this channel names documents by id and nothing else (#7063). One payload goes to
// every reader, the other party on a proposal among them, and a document response names its creator,
// which a proposal withholds from the other party (#6623).
const callbacks = withCallbacks()
  // One upload request publishes one of these per recipient, carrying only the ids that recipient
  // may read. Invalidating them together is what keeps a ten-file upload to one list refetch.
  .add("documents-created", invalidateDocumentIds)
  // A change to the document row, or to something it is read with, such as the end of its file's
  // malware scan (#6992).
  .add("documents-changed", invalidateDocumentIds)
  .add("documents-deleted", (payload: unknown) => (dispatch) => {
    const documentIds = toDocumentIds(payload);
    if (!documentIds.length) return;
    dispatch(documentsApi.util.invalidateTags([
      ...documentIds.map((id) => ({ type: "Document" as const, id: id.value })),
      { type: "Document", id: "LIST" },
    ]));
    for (const documentId of documentIds) {
      dispatch(cleanupProposalDeletedDocumentReferences(documentId));
      dispatch(cleanupWorkAgreementDeletedDocumentReferences(documentId));
    }
    dispatch(proposalsApi.util.invalidateTags([{ type: "Proposal" }]));
    dispatch(workAgreementsApi.util.invalidateTags([{ type: "WorkAgreement" }]));
  });

export const documentsAblyReduxMiddleware = reduxAblyMiddleware({
  callbacks,
  channelName: ablyChannelNamesForSession.documents,
  connectionHealth: { key: "documents", label: "Documents" },
  shouldConnectionStartImmediately: true,
});

